import { getVentureBuilderRequestWallet } from 'packages/service_request_repository/ventureBuilder';
import { SERVICE_REQUEST_API, SERVICE_REQUEST_ITEM_API } from 'packages/service_request_repository/utils/constants';
import httpClient, { ApiResponseType, FORM_DATA_CONFIG, queryBuilder } from 'packages/http_client';
import { ServicePriceType } from 'packages/service_repository';
import { unwrap } from '../api';
import { convertManagedStartupResponse } from '../utils/converters';
import {
  ItemApiResponseType, ListApiResponseType, ManagedStartupApiType, ManagedStartupType,
} from '../types';
import {
  PortalQuoteDecision, PortalDraftChange, PortalOrder, PortalRequest, PortalRequestItem,
  PortalScope, PortalService, PricedPortalService,
} from './types';
import {
  draftFingerprint, isEligibleProvider, orderPrice, UncertainOrderError,
} from './utils';

const STARTUPS = '/api/d/venturebuildermanagedstartup';
const REQUESTS = SERVICE_REQUEST_API;
const ITEMS = SERVICE_REQUEST_ITEM_API;

export const context = (startupId: number) => {
  if (!Number.isSafeInteger(startupId) || startupId < 1) throw new Error('Choose a startup');
  return { headers: { 'X-VentureBuilder-Context': String(startupId) } };
};

export const readPages = async <T extends { id: number }>(
  url: string, params: Record<string, unknown>, startupId?: number,
): Promise<T[]> => {
  const rows: T[] = [];
  const seen = new Set<number>();
  let offset = 0;
  let more = true;
  while (more) {
    const response = await httpClient.get<ApiResponseType<ListApiResponseType<T>>>(url, {
      ...(startupId === undefined ? {} : context(startupId)),
      params: {
        ...params, $limit: 100, $offset: offset, $order: 'id',
      },
    });
    const { result } = unwrap(response);
    if (!Array.isArray(result)) throw new Error('Unable to load the list. Please retry');
    result.forEach((row) => {
      if (!row.id || seen.has(row.id)) throw new Error('Unable to load the complete list. Please retry');
      seen.add(row.id);
      rows.push(row);
    });
    more = result.length === 100;
    offset += result.length;
  }
  return rows;
};

export const getPortalPortfolio = async (scope: PortalScope): Promise<ManagedStartupType[]> => {
  if (!scope.userId || !Number.isSafeInteger(scope.ventureBuilderId) || scope.ventureBuilderId < 1) {
    throw new Error('Venture Builder account is unavailable');
  }
  const rows = await readPages<ManagedStartupApiType>(STARTUPS, {
    venture_builder_id: scope.ventureBuilderId, $preload: 1,
  });
  if (rows.some((row) => row.ventureBuilderID !== scope.ventureBuilderId)) {
    throw new Error('Unable to verify the startup portfolio');
  }
  return rows.map(convertManagedStartupResponse);
};

const ownedStartup = async (scope: PortalScope, startupId: number) => {
  context(startupId);
  const response = await httpClient.get<ApiResponseType<ItemApiResponseType<ManagedStartupApiType>>>(`${STARTUPS}/${startupId}`, { params: { $preload: 1 } });
  const { result } = unwrap(response);
  if (result?.id !== startupId || result.ventureBuilderID !== scope.ventureBuilderId) {
    throw new Error('This startup is not in your portfolio');
  }
  return convertManagedStartupResponse(result);
};

export const getPortalServices = async (): Promise<PortalService[]> => {
  // The existing catalogue has no VB eligibility flag. Resolve the documented
  // provider names, never IDs from a particular environment. Confirm with Ivan.
  const providers = (await Promise.all(['clara', 'wework'].map(($q) => (
    readPages<{ id: number, name: string }>('/api/d/serviceprovider', { $q })
  )))).flat().filter(({ name }) => isEligibleProvider(name));
  const ids = Array.from(new Set(providers.map(({ id }) => id)));
  if (!ids.length) return [];
  const params = queryBuilder({ active: 1, $preload: 1 }).in({ service_provider_id: ids }).toObject();
  const services = await readPages<PortalService>('/api/d/service', params);
  return services.filter((service) => service.active && ids.includes(service.serviceProviderID!)
    && isEligibleProvider(service.serviceProvider?.name));
};

export const getPortalService = async (id: number): Promise<PricedPortalService> => {
  const response = await httpClient.get<ApiResponseType<ItemApiResponseType<PortalService>>>(`/api/d/service/${id}`, { params: { $preload: 1 } });
  const { result } = unwrap(response);
  if (result?.id !== id || !result.active || !isEligibleProvider(result.serviceProvider?.name)) {
    throw new Error('This service is not available for Venture Builders');
  }
  const prices = await readPages<ServicePriceType>('/api/d/serviceprice', { service_id: id, $preload: 1 });
  const servicePrice = prices[0]; // Same price record used by the existing service order flow.
  if (!servicePrice || servicePrice.serviceID !== id || servicePrice.toPrice === undefined
    || !Number.isFinite(servicePrice.toPrice) || servicePrice.toPrice < 0) {
    throw new Error('Service pricing is unavailable. Please retry');
  }
  return { ...result, servicePrice };
};

export const getPortalRequests = async (startup: ManagedStartupType): Promise<PortalRequest[]> => {
  const rows = await readPages<PortalRequest>(REQUESTS, {
    venture_builder_managed_startup_id: startup.id, $preload: 1,
  }, startup.id);
  if (rows.some((row) => row.ventureBuilderManagedStartupID !== startup.id)) {
    throw new Error('Unable to verify the requests for this startup');
  }
  return rows.map((row) => ({ ...row, startup }));
};

export const getPortalRequestItems = async (startupId: number, requestId: number) => {
  const items = await readPages<PortalRequestItem>(ITEMS, { service_request_id: requestId, $preload: 1 }, startupId);
  if (items.some((item) => item.serviceRequestID !== requestId)) {
    throw new Error('Unable to verify the services in this request');
  }
  try {
    items.forEach((item) => { if (item.details) JSON.parse(item.details); });
  } catch {
    throw new Error('Unable to read the saved service details. Please retry');
  }
  return items;
};

const createRecord = async (url: string, body: Record<string, unknown>, startupId: number) => {
  const config = { ...FORM_DATA_CONFIG, ...context(startupId) };
  let response;
  try {
    response = await httpClient.post<ApiResponseType<{ status: string, id?: number[] }>>(url, body, config);
  } catch {
    throw new UncertainOrderError();
  }
  if (response.error && (!response.httpStatus || response.httpStatus >= 500)) throw new UncertainOrderError();
  const id = unwrap(response).id?.[0];
  if (!id || !Number.isSafeInteger(id)) throw new UncertainOrderError();
  return id;
};

export const addPortalOrder = async (scope: PortalScope, order: PortalOrder) => {
  // Capture context in arguments, never in a global header or shared startup/cart state.
  const [startup, service] = await Promise.all([
    ownedStartup(scope, order.startupId), getPortalService(order.serviceId),
  ]);
  if (service.categoryID === 1 && !order.officeType) throw new Error('Choose an office type');
  const total = orderPrice(service, order);
  const requests = await getPortalRequests(startup);
  const draft = requests.find((request) => request.status === 1
    && request.serviceProviderID === service.serviceProviderID);
  const requestId = draft?.id || await createRecord(REQUESTS, {
    _venture_builder_managed_startup_id: startup.id,
    _service_provider_id: service.serviceProviderID,
    _status: 1,
  }, startup.id);
  await createRecord(ITEMS, {
    _service_request_id: requestId,
    _service_id: service.id,
    _service_price_id: service.servicePrice.id,
    _quantity: order.quantity,
    _price: total,
    _details: JSON.stringify(service.categoryID === 1
      ? { officeType: order.officeType, noOfDesk: order.desks } : {}),
  }, startup.id);
  return { requestId, startupId: startup.id };
};

const readReviewedDraft = async (
  scope: PortalScope,
  startupId: number,
  requestId: number,
  reviewedItems: PortalRequestItem[],
) => {
  await ownedStartup(scope, startupId);
  const response = await httpClient.get<ApiResponseType<ItemApiResponseType<PortalRequest>>>(`${REQUESTS}/${requestId}`, context(startupId));
  const request = unwrap(response).result;
  if (request?.id !== requestId || request.ventureBuilderManagedStartupID !== startupId || request.status !== 1) {
    throw new Error('This draft is no longer available. Refresh Requests');
  }
  const items = await getPortalRequestItems(startupId, requestId);
  if (draftFingerprint(items) !== draftFingerprint(reviewedItems)) {
    throw new Error('This draft has changed. Review the updated services and total before trying again');
  }
  return items;
};

export const submitPortalRequest = async (
  scope: PortalScope,
  startupId: number,
  requestId: number,
  reviewedItems: PortalRequestItem[],
) => {
  const items = await readReviewedDraft(scope, startupId, requestId, reviewedItems);
  if (!items.length || items.some((item) => item.serviceRequestID !== requestId
    || !Number.isFinite(item.price) || item.price < 0)) throw new Error('The draft has no valid services');
  const total = Math.round(items.reduce((sum, item) => sum + item.price, 0) * 100) / 100;
  unwrap(await httpClient.put<ApiResponseType<{ status: string }>>(`${REQUESTS}/${requestId}`, {
    _status: 2, _total_price: total,
  }, { ...FORM_DATA_CONFIG, ...context(startupId) }));
};

export const changePortalDraft = async (scope: PortalScope, action: PortalDraftChange) => {
  const {
    startupId, requestId, reviewedItems, change,
  } = action;
  const items = await readReviewedDraft(scope, startupId, requestId, reviewedItems);
  const removeItem = async (itemId: number) => {
    unwrap(await httpClient.delete<ApiResponseType<{ status: string }>>(`${ITEMS}/${itemId}`, context(startupId)));
  };
  if (change.kind === 'discard') {
    let remaining = items;
    // Existing dAPI only supports individual writes. Stop on a partial failure or concurrent change.
    for (const item of items) {
      await readReviewedDraft(scope, startupId, requestId, remaining);
      await removeItem(item.id);
      remaining = remaining.filter(({ id }) => id !== item.id);
    }
    await readReviewedDraft(scope, startupId, requestId, []);
    unwrap(await httpClient.delete<ApiResponseType<{ status: string }>>(`${REQUESTS}/${requestId}`, context(startupId)));
    return;
  }
  const item = items.find(({ id }) => id === change.itemId);
  if (!item) throw new Error('This item is no longer in the draft. Refresh Requests');
  if (change.kind === 'remove') {
    await removeItem(item.id);
    return;
  }
  if (!Number.isSafeInteger(change.quantity) || change.quantity < 1 || item.quantity <= 0
    || !Number.isFinite(item.price) || item.price < 0) throw new Error('Check the quantity and price');
  // Preserve the saved estimate, including office desks/type, when changing quantity/months.
  const price = Math.round((item.price / item.quantity) * change.quantity * 100) / 100;
  if (!Number.isFinite(price)) throw new Error('Check the quantity and price');
  unwrap(await httpClient.put<ApiResponseType<{ status: string }>>(`${ITEMS}/${item.id}`, {
    _quantity: change.quantity, _price: price,
  }, { ...FORM_DATA_CONFIG, ...context(startupId) }));
};

export const getPortalRequest = async (startupId: number, requestId: number) => {
  const response = await httpClient.get<ApiResponseType<ItemApiResponseType<PortalRequest>>>(`${REQUESTS}/${requestId}`, {
    ...context(startupId), params: { $preload: 1 },
  });
  const request = unwrap(response).result;
  if (request?.id !== requestId || request.ventureBuilderManagedStartupID !== startupId) {
    throw new Error('Unable to verify this request owner');
  }
  return request;
};

export const decidePortalQuote = async (scope: PortalScope, decision: PortalQuoteDecision) => {
  const {
    startupId, requestId, reviewedItems, reviewedTotal, approve, reason,
  } = decision;
  await ownedStartup(scope, startupId);
  const request = await getPortalRequest(startupId, requestId);
  if (request.status !== 3 || request.totalPrice !== reviewedTotal) {
    throw new Error('This quotation has changed. Review the updated request before trying again');
  }
  const items = await getPortalRequestItems(startupId, requestId);
  const total = Math.round(items.reduce((sum, item) => sum + item.price, 0) * 100) / 100;
  if (!items.length || !Number.isFinite(total) || total < 0 || total !== request.totalPrice
    || items.some((item) => !Number.isFinite(item.price) || item.price < 0)
    || draftFingerprint(items) !== draftFingerprint(reviewedItems)) {
    throw new Error('This quotation has changed. Review the updated services and total before trying again');
  }
  if (approve) {
    const wallet = await getVentureBuilderRequestWallet(startupId, true);
    if (wallet.balance < total) throw new Error('This startup does not have sufficient funds to cover this request');
  }
  unwrap(await httpClient.put<ApiResponseType<{ status: string }>>(
    `${REQUESTS}/${requestId}`,
    approve
      ? { _status: 4 } : { _status: 6, _rejected_reason: (reason || '').trim() },
    { ...FORM_DATA_CONFIG, ...context(startupId) },
  ));
};
